// Timeline layout — time as position across the day's lane (derived from the configured
// sessions: 08:00–18:00 for the default 08:30–13:00 / 14:00–17:30 day; 09:00–18:00 as the
// fallback). Every assignment is a true-time bar (time overrides and doctor part-sessions
// become visible spatially), a person's AM and PM pack into ONE shared row (they can never
// overlap in time), the mid-day break renders as a hatched band and every hour gets a
// hairline gridline. Dropping left of the lane's boundary (the midpoint of the break)
// assigns the first session, right of it the second.
import { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { resolveFlagIcon } from "@/lib/work-plan-flag-icons";
import { flagTone, toggleFlagId } from "@/lib/work-plan-flags";
import {
  CellPicker,
  CountNumeral,
  FlagGlyphs,
  IdentityAvatar,
  MoveMarker,
  TimeField,
  clinicPickerOptions,
  staffPickerOptions,
} from "./planner-bits";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DEFAULT_TIMELINE_LANE, identityTone, timelinePct, timelineSpan } from "./work-plan-utils";

const LABEL_WIDTH = 200;
const BAR_HEIGHT = 24;
const ROW_PITCH = 29;
const TRACK_PAD = 8;

// One hairline per hour: the gradient repeats every 1/N of the track, N = lane hours.
function hourGrid(lane) {
  const hours = Math.max(1, Math.round(lane.spanMin / 60));
  return `repeating-linear-gradient(90deg, var(--border) 0 1px, transparent 1px calc(100% / ${hours}))`;
}
const HATCH = "repeating-linear-gradient(135deg, var(--muted) 0 5px, transparent 5px 10px)";

const DOCTOR_BAR =
  "bg-sky-200/70 text-sky-950 shadow-[inset_3px_0_0_var(--color-sky-500)] dark:bg-[oklch(0.36_0.07_248)] dark:text-[oklch(0.92_0.02_248)] dark:shadow-[inset_3px_0_0_oklch(0.62_0.14_248)]";
const SUPPORT_BAR =
  "border border-border bg-card text-foreground dark:border-[oklch(0.36_0.03_264/0.8)] dark:bg-[oklch(0.28_0.02_264)]";

function TimelineBar({ entry, session, lane, flagDefs, canManage, actions }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const span = timelineSpan(entry.start, entry.end, lane);
  if (!span) return null;
  const style = {
    left: `${span.left}%`,
    width: `${span.width}%`,
    top: entry.top,
    height: BAR_HEIGHT,
  };
  const flag = entry.dayFlag;
  const flagNames = (entry.flags || []).map((item) => item.label).join(", ");
  const content = (
    <div
      className={cn(
        "absolute flex items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-[7px] px-1.5 text-[11.5px]",
        entry.locked ? cn(DOCTOR_BAR, "cursor-default") : SUPPORT_BAR,
        !entry.locked && canManage && "cursor-pointer transition-colors duration-120 hover:bg-muted dark:hover:bg-[oklch(0.32_0.03_264)]",
      )}
      style={style}
      title={[
        entry.locked
          ? `${entry.title} — rostered in Sessions, read-only`
          : `${entry.title} · ${entry.start}–${entry.end}`,
        flagNames,
        flag?.title,
      ]
        .filter(Boolean)
        .join(" · ")}
    >
      <IdentityAvatar id={entry.avatarId} name={entry.avatarName} photoUrl={entry.avatarPhotoUrl} size={16} />
      <span className="truncate font-bold">{entry.title}</span>
      <FlagGlyphs flags={entry.flags} />
      <span className="shrink-0 text-[10.5px] tabular-nums opacity-70">
        {entry.start}–{entry.end}
      </span>
      <MoveMarker flag={flag} muted={entry.locked} className="text-[10.5px]" />
    </div>
  );

  if (entry.locked || !canManage) return content;
  return (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>{content}</PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <p className="px-1 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {entry.title} · {session.label}
        </p>
        <TimelineBarMenu
          entry={entry}
          session={session}
          flagDefs={flagDefs}
          onClose={() => setMenuOpen(false)}
          actions={actions}
        />
      </PopoverContent>
    </Popover>
  );
}

function TimelineBarMenu({ entry, session, flagDefs, onClose, actions }) {
  const [from, setFrom] = useState(entry.start);
  const [to, setTo] = useState(entry.end);
  const defs = (flagDefs || []).filter((def) => def?.id);
  const activeFlags = new Set(entry.flagIds || []);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <div className="flex-1">
          <TimeField value={from} onChange={(event) => setFrom(event.target.value)} className="bg-background" />
        </div>
        <div className="flex-1">
          <TimeField value={to} onChange={(event) => setTo(event.target.value)} className="bg-background" />
        </div>
      </div>
      {defs.length > 0 ? (
        // Flag toggles as chips; the popover stays open so several can be flipped in a row
        // (the bar re-renders with the new ids through the optimistic applyLocal).
        <div className="flex flex-wrap gap-1">
          {defs.map((def) => {
            const Icon = resolveFlagIcon(def.icon);
            const on = activeFlags.has(def.id);
            return (
              <button
                key={def.id}
                type="button"
                aria-pressed={on}
                title={def.description || def.label}
                className={cn(
                  "flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold transition-colors",
                  on
                    ? flagTone(def.colorId).chip
                    : "border border-border text-muted-foreground hover:bg-muted",
                )}
                onClick={() => actions.setFlags(entry.posting, toggleFlagId(entry.flagIds, def.id))}
              >
                <Icon className={cn("size-3", on ? "" : flagTone(def.colorId).glyph)} />
                <span className="max-w-28 truncate">{def.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      <div className="flex gap-1.5">
        <button
          type="button"
          className="flex-1 rounded-md bg-primary px-2 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-50"
          disabled={!from || !to || to <= from}
          onClick={() => {
            onClose();
            actions.setTimes(entry.posting, from, to);
          }}
        >
          Apply time
        </button>
        <button
          type="button"
          className="rounded-md border border-border px-2 py-1.5 text-xs hover:bg-muted"
          title={`Reset to ${session.start} – ${session.end}`}
          onClick={() => {
            onClose();
            actions.resetTimes(entry.posting);
          }}
        >
          Reset
        </button>
        <button
          type="button"
          className="rounded-md border border-rose-200 px-2 py-1.5 text-xs text-rose-600 hover:bg-rose-50 dark:border-rose-900 dark:hover:bg-rose-950/40"
          onClick={() => {
            onClose();
            actions.remove(entry.posting);
          }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

/** The hatched band between the sessions (the 13:00–14:00 break on a default day). */
function BreakBand({ sessions, lane }) {
  if (sessions.length < 2) return null;
  const span = timelineSpan(sessions[0].end, sessions[1].start, lane);
  if (!span) return null;
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-y-0"
      style={{ left: `${span.left}%`, width: `${span.width}%`, background: HATCH }}
    />
  );
}

function TimelineLane({ row, sessions, lane, mode, data, appointmentsSupported, canManage, onAssign, actions }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `lane:${row.id}`,
    data: { rowId: row.id, lane: true },
    disabled: row.locked || !canManage,
  });
  const [pickerFor, setPickerFor] = useState(null); // session key or null

  // AM and PM pack into SHARED rows by index — a person's two half-days sit on one line,
  // and the lane only grows with the busier session. Gaps stack after that session's bars.
  const bars = [];
  const gaps = [];
  let laneRows = 1;
  for (const session of sessions) {
    const cell = row.cells[session.key];
    let slot = 0;
    for (const entry of cell.items) {
      bars.push({ ...entry, top: TRACK_PAD + slot * ROW_PITCH, sessionDef: session });
      slot += 1;
    }
    for (const gap of cell.gaps) {
      gaps.push({ ...gap, sessionDef: session, top: TRACK_PAD + slot * ROW_PITCH });
      slot += 1;
    }
    laneRows = Math.max(laneRows, slot);
  }
  const laneHeight = 2 * TRACK_PAD + laneRows * ROW_PITCH;
  const isEmpty = bars.length === 0 && gaps.length === 0;
  const dayTotal = sessions.reduce((sum, session) => sum + (row.cells[session.key]?.apptCount || 0), 0);

  const pickerSession = pickerFor ? sessions.find((s) => s.key === pickerFor) : null;
  const excludeIds = pickerSession
    ? row.cells[pickerSession.key].items.map((entry) => (mode === "clinic" ? entry.personId : entry.clinicId))
    : [];
  const pickerOptions = pickerSession
    ? mode === "clinic"
      ? staffPickerOptions(data.staff, excludeIds, data.roleView)
      : clinicPickerOptions(data.clinics, excludeIds)
    : [];

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "relative flex-1",
        isOver && "bg-primary/5 outline-dashed outline-1 -outline-offset-2 outline-primary",
      )}
      style={{ height: laneHeight, background: hourGrid(lane) }}
    >
      <BreakBand sessions={sessions} lane={lane} />
      {isEmpty ? (
        <span className="absolute left-2.5 top-[10px] text-[11.5px] text-muted-foreground/60">
          {mode === "clinic" ? "— no staff assigned" : "— not assigned"}
          {appointmentsSupported && mode === "clinic"
            ? ` (${dayTotal} appt${dayTotal === 1 ? "" : "s"})`
            : ""}
        </span>
      ) : null}
      {gaps.map((gap) => {
        const span = timelineSpan(gap.sessionDef.start, gap.sessionDef.end, lane);
        if (!span) return null;
        return (
          <button
            key={gap.id}
            type="button"
            className="absolute flex items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-[7px] border-[1.5px] border-dashed border-rose-300 bg-rose-50/90 px-1.5 text-[11.5px] font-semibold text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300"
            style={{ left: `${span.left}%`, width: `${span.width}%`, top: gap.top, height: BAR_HEIGHT }}
            title="Needs cover"
            onClick={() => {
              if (canManage && !gap.whoIsDoctor) setPickerFor(gap.sessionDef.key);
            }}
          >
            <AlertTriangle className="size-3 shrink-0" />
            <span className="truncate">Needs cover — {gap.whoFirstName} on MC</span>
          </button>
        );
      })}
      {bars.map((entry) => (
        <TimelineBar
          key={entry.key}
          entry={entry}
          session={entry.sessionDef}
          lane={lane}
          flagDefs={data.flagDefs}
          canManage={canManage}
          actions={actions}
        />
      ))}
      {pickerSession ? (
        <div className="absolute left-[30%] top-2">
          <CellPicker
            mode={mode}
            options={pickerOptions}
            open
            onOpenChange={(next) => {
              if (!next) setPickerFor(null);
            }}
            onPick={(pickedId) => {
              setPickerFor(null);
              onAssign(row, pickerSession.key, pickedId);
            }}
          >
            <span />
          </CellPicker>
        </div>
      ) : null}
    </div>
  );
}

export default function TimelinePlanner({
  rows,
  sessions,
  lane = DEFAULT_TIMELINE_LANE,
  mode,
  data,
  appointmentCounts,
  canManage,
  onAssign,
  actions,
}) {
  const appointmentsSupported = Boolean(appointmentCounts);
  return (
    <div>
      {/* Hour axis, offset by the lane-label column so ticks align with the tracks. */}
      <div className="sticky top-0 z-[5] bg-background pt-3.5" style={{ paddingLeft: LABEL_WIDTH }}>
        <div className="relative h-5">
          {lane.hours.map((hour) => (
            <span
              key={hour}
              className="absolute top-0 -translate-x-1/2 text-[10.5px] tabular-nums text-muted-foreground"
              style={{ left: `${timelinePct(hour * 60, lane)}%` }}
            >
              {String(hour).padStart(2, "0")}:00
            </span>
          ))}
        </div>
      </div>
      {rows.map((row, index) => (
        <div
          key={row.id}
          className={cn("grid border-t border-border", index === rows.length - 1 && "border-b")}
          style={{ gridTemplateColumns: `${LABEL_WIDTH}px 1fr` }}
        >
          <div className="flex items-start gap-2 py-2.5 pr-3">
            {mode === "clinic" ? (
              <span
                className="mt-0.5 h-5 w-[3px] shrink-0 rounded-full"
                style={{ background: identityTone(row.id).solid }}
              />
            ) : (
              <IdentityAvatar id={row.id} name={row.title} photoUrl={row.photoUrl} size={20} />
            )}
            <span className="min-w-0">
              <span
                className={cn(
                  "block truncate text-[12.5px] font-bold leading-tight",
                  row.locked && "font-normal text-muted-foreground",
                )}
                title={row.locked ? `${row.sub} · from Sessions — read-only here` : row.sub || undefined}
              >
                {row.title}
              </span>
              {mode === "clinic" && appointmentsSupported ? (
                <span className="mt-1 flex gap-2.5">
                  {sessions.map((session) => (
                    <CountNumeral
                      key={session.key}
                      count={row.cells[session.key]?.apptCount || 0}
                      label={session.label}
                    />
                  ))}
                </span>
              ) : (
                <span className="block truncate text-[10.5px] text-muted-foreground">{row.sub}</span>
              )}
            </span>
          </div>
          <TimelineLane
            row={row}
            sessions={sessions}
            lane={lane}
            mode={mode}
            data={data}
            appointmentsSupported={appointmentsSupported}
            canManage={canManage}
            onAssign={onAssign}
            actions={actions}
          />
        </div>
      ))}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className={cn("h-2.5 w-4 rounded-[3px]", DOCTOR_BAR)} />
          doctor (Sessions)
        </span>
        <span className="flex items-center gap-1.5">
          <span className={cn("h-2.5 w-4 rounded-[3px]", SUPPORT_BAR)} />
          support{canManage ? " (click a bar to change hours or flags)" : ""}
        </span>
        {sessions.length >= 2 ? (
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-4 rounded-[3px]" style={{ background: HATCH }} />
            {sessions[0].end}–{sessions[1].start} break
          </span>
        ) : null}
        <span className="text-amber-600 dark:text-amber-400">→ CLE — moves on after this session</span>
      </div>
    </div>
  );
}
